import { type Getter } from "jotai";
import { Node } from "prosemirror-model";
import { EditorState, type Plugin } from "prosemirror-state";

import { schema } from "@moment/prosemirror-schema";

import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";

export declare type Json = string | number | boolean | null | Json[] | { [key: string]: Json };
export declare type JsonObject = { [key: string]: Json };

export type CreateEditorStateInput = {
	doc: JsonObject;
	plugins: Plugin[];
	ssr?: boolean;
};

/**
 * Creates a ProseMirror EditorState.
 *
 * NOTE: We'd like for this function to handle plugin creation, but the `@moment/state` package does
 * not have access to all the plugins, which often even include React code (*.e.g.*, the
 * `imageDropPlugin`).
 */
export const createEditorState = (input: CreateEditorStateInput) => {
	const { doc: rawDoc, ssr = false, plugins } = input;
	// If this page is being SSR'ed, we want to remove compute_cells from the document
	// There is probably a better way to do this
	const doc = ssr
		? {
				...rawDoc,
				content: (rawDoc?.["content"] as JsonObject[])?.filter(
					(content) => content?.["type"] !== schema.nodes.compute_cell.name
				),
			}
		: rawDoc;

	return EditorState.create({ doc: Node.fromJSON(schema, doc), plugins });
};

export const resolveCurrentPagePath = async (
	get: Getter,
	documentId: string,
	pageId: string,
	fallbackPagePath?: string
): Promise<string | undefined> => {
	const metadata = await get($documentMetadata(metadataKeyForDocsRoute(documentId)));
	return metadata?.pagesMetadata.find((page) => page.id === pageId)?.path ?? fallbackPagePath;
};
